import React from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Modal,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  Stack,
  TextField,
  Tooltip,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import Iconify from '../../../components/Iconify';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import { formatDate } from '../../../services/functions';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';
import { useMutation } from 'react-fetching-library';
import { changeDocumentsSigningDates } from '../../../services/request';
import { remove } from 'lodash';

export default function ActionsDropdownButton({
  idList,
  selectedOrdersIds,
  onRefresh
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [openExtendDocsDurationModal, setOpenExtendDocsDurationModal] =
    React.useState(false);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenExtendDocsDurationModal = () => {
    setOpenExtendDocsDurationModal(true);
    handleClose();
  };

  return (
    <>
      <Button
        sx={{ width: '100%%', display: 'flex', flexDirection: 'row' }}
        variant="contained"
        fullWidth
        endIcon={<ArrowDropDownIcon />}
        onClick={handleClick}
      >
        Akcje
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleOpenExtendDocsDurationModal}>
          <ListItemIcon>
            <Iconify
              icon={'mdi:calendar-clock-outline'}
              width={20}
              height={20}
            />
          </ListItemIcon>
          <ListItemText>Termin podpisu dokumentów</ListItemText>
        </MenuItem>
      </Menu>

      {openExtendDocsDurationModal ? (
        <ExtendDocsDurationModal
          open={openExtendDocsDurationModal}
          handleClose={() => setOpenExtendDocsDurationModal(false)}
          idList={idList}
          selectedOrdersIds={selectedOrdersIds}
          onRefresh={onRefresh}
        />
      ) : null}
    </>
  );
}

const extendDocsDurationModalstyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

function ExtendDocsDurationModal({
  open,
  handleClose,
  idList,
  selectedOrdersIds,
  onRefresh
}) {
  const { loading, mutate } = useMutation(changeDocumentsSigningDates);

  const validationSchema = Yup.object({
    agreement_checked: Yup.boolean(),
    agreement_date: Yup.string().when('agreement_checked', {
      is: true,
      then: Yup.string().required('Data umowy jest wymagana'),
      otherwise: Yup.string().nullable()
    }),
    annex_checked: Yup.boolean(),
    annex_date: Yup.string().when('annex_checked', {
      is: true,
      then: Yup.string().required('Data aneksu jest wymagana'),
      otherwise: Yup.string().nullable()
    }),
    qc_checked: Yup.boolean(),
    qc_date: Yup.string().when('qc_checked', {
      is: true,
      then: Yup.string().required('Data karty kwalifikacyjnej jest wymagana'),
      otherwise: Yup.string().nullable()
    }),
    only_extend: Yup.boolean(),
    remove_signiature: Yup.boolean()
  });

  const formik = useFormik({
    initialValues: {
      agreement_checked: false,
      agreement_date: '',
      annex_checked: false,
      annex_date: '',
      qc_checked: false,
      qc_date: '',
      only_extend: true,
      remove_signiature: false
    },
    validationSchema,
    onSubmit: async values => {
      await handleSubmit(values);
      handleClose();
    }
  });

  const handleSubmit = async values => {
    // Tworzymy wynikowy słownik tylko z zaznaczonych dokumentów
    const docs = {};
    if (values.agreement_checked) {
      docs.agreement = values.agreement_date;
    }
    if (values.annex_checked) {
      docs.annex = values.annex_date;
    }
    if (values.qc_checked) {
      docs.qualification_card = values.qc_date;
    }

    const { payload, error } = await mutate({
      order_ids: selectedOrdersIds,
      documents: docs,
      only_extend: values.only_extend,
      remove_signiature: values.remove_signiature
    });
    if (!error) {
      toast.success('Termin podpisu dokumentów został zaktualizowany');
    } else {
      toast.error('Nie udało się zaktualizować terminu podpisu dokumentów');
    }
  };

  const disableSubmit =
    !(formik.values.agreement_checked || formik.values.annex_checked || formik.values.qc_checked) ||
    selectedOrdersIds.length === 0;

  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          ...extendDocsDurationModalstyle,
          // minHeight: '800px',
          minWidth: '600px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <CardHeader
            title={`Zmień termin na podpis dokumentów przez klienta dla ${selectedOrdersIds.length} zamówień`}
          />
          <CardContent
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              pt: 2
            }}
            spacing={2}
          >
            {/* Umowa */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ mb: 3 }}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="agreement_checked"
                    checked={formik.values.agreement_checked}
                    onChange={formik.handleChange}
                  />
                }
                label="Umowa"
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
                spacing={1}
              >
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disabled={!formik.values.agreement_checked}
                    disablePast={true}
                    name="agreement_date"
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    label="Umowa do"
                    value={formik.values.agreement_date}
                    onChange={val => formik.setFieldValue('agreement_date', formatDate(val))}
                    renderInput={params => (
                      <TextField
                        size="small"
                        {...params}
                        error={
                          formik.touched.agreement_date &&
                          Boolean(formik.errors.agreement_date)
                        }
                        helperText={
                          formik.touched.agreement_date &&
                          formik.errors.agreement_date
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>

            {/* annex */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ mb: 3 }}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="annex_checked"
                    checked={formik.values.annex_checked}
                    onChange={formik.handleChange}
                  />
                }
                label="Ostatni aneks"
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disabled={!formik.values.annex_checked}
                    disablePast={true}
                    name="annex_date"
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    label="Aneks do"
                    value={formik.values.annex_date}
                    onChange={val => formik.setFieldValue('annex_date', formatDate(val))}
                    renderInput={params => (
                      <TextField
                        size="small"
                        {...params}
                        error={
                          formik.touched.annex_date &&
                          Boolean(formik.errors.annex_date)
                        }
                        helperText={
                          formik.touched.annex_date && formik.errors.annex_date
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>

            {/* qualification card */}
            <Stack
              direction="row"
              spacing={2}
              sx={{ mb: 3 }}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name="qc_checked"
                    checked={formik.values.qc_checked}
                    onChange={formik.handleChange}
                  />
                }
                label="Karta kwalifikacyjna"
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disabled={!formik.values.qc_checked}
                    disablePast={true}
                    name="qc_date"
                    inputFormat="yyyy-MM-dd"
                    mask="____-__-__"
                    label="Karta do"
                    value={formik.values.qc_date}
                    onChange={val => formik.setFieldValue('qc_date', formatDate(val))}
                    renderInput={params => (
                      <TextField
                        size="small"
                        {...params}
                        error={
                          formik.touched.qc_date &&
                          Boolean(formik.errors.qc_date)
                        }
                        helperText={
                          formik.touched.qc_date && formik.errors.qc_date
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Stack>
            </Stack>

            <Box height={40} />
            <FormControlLabel
              control={
                <Checkbox
                  name="only_extend"
                  checked={formik.values.only_extend}
                  onChange={formik.handleChange}
                />
              }
              label={
                <Stack direction="row" alignItems="center">
                  Nie skracaj terminów
                  <Tooltip
                    title={
                      'Jeśli zaznaczone, w przypadku gdy nowo podana data będzie wcześniejsza niż obecnie ustawiona, termin nie zostanie zmieniony.'
                    }
                    placement="right"
                  >
                    <InfoIcon sx={{ ml: 1, fontSize: 24 }} color="info" />
                  </Tooltip>
                </Stack>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="remove_signiature"
                  checked={formik.values.remove_signiature}
                  onChange={formik.handleChange}
                />
              }
              label={'Usuń podpisy z dokumentów (jeśli istnieją)'}
            />
          </CardContent>

          {/* Przycisk na dole */}
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ p: 2 }}
          >
            <Button type="submit" variant="contained" disabled={disableSubmit}>
              Zatwierdź
            </Button>
          </Stack>
        </form>
      </Card>
    </Modal>
  );
}
